import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import DocumentWorkflowBuilder, {
  WorkflowPage,
  workflowDownload,
} from "./DocumentWorkflowBuilder";
import { TotoAdaptiveDock } from "@/components/toto/TotoAdaptiveSurface";

const message = (e) =>
  typeof e.response?.data?.detail === "string"
    ? e.response.data.detail
    : "The document could not be loaded. Please try again.";
export default function WorkflowDocumentView({ initialDocument, onSaved }) {
  const navigate = useNavigate();
  const { id: routeId } = useParams(),
    [search] = useSearchParams(),
    { user } = useAuth();
  const id = initialDocument?.id || routeId,
    version = Number(search.get("version") || initialDocument?.version || 0),
    employeeId = search.get("employee_id") || user?.user_id;
  const [doc, setDoc] = useState(initialDocument || null),
    [preview, setPreview] = useState(null),
    [page, setPage] = useState(0),
    [values, setValues] = useState({}),
    [completion, setCompletion] = useState(null),
    [counter, setCounter] = useState(null),
    [editing, setEditing] = useState(
      !!initialDocument?.can_edit &&
        initialDocument?.status === "draft" &&
        !search.get("version"),
    ),
    [consent, setConsent] = useState(false),
    [name, setName] = useState(""),
    [busy, setBusy] = useState(false),
    [error, setError] = useState("");
  const contextKey = `${id}:${version}:${employeeId}`;
  const contextRef = useRef(contextKey);
  contextRef.current = contextKey;
  useLayoutEffect(() => {
    setDoc(null);
    setPreview(null);
    setCompletion(null);
    setCounter(null);
    setValues({});
    setConsent(false);
    setName("");
    setPage(0);
    setBusy(false);
  }, [id, version, employeeId]);

  const pinned = !!search.get("version"),
    self = employeeId === user?.user_id;
  useEffect(() => {
    let live = true;
    setError("");
    (async () => {
      try {
        let next = initialDocument;
        if (pinned)
          next = (
            await api.get(`/document-workflows/${id}/version/${version}`, {
              params: { employee_id: employeeId },
            })
          ).data;
        else if (!next) next = (await api.get(`/documents/${id}`)).data;
        if (live) {
          setDoc(next);
          setEditing(!!next?.can_edit && next?.status === "draft" && !pinned);
        }
      } catch (e) {
        if (live) setError(message(e));
      }
    })();
    return () => {
      live = false;
    };
  }, [id, version, employeeId, pinned, initialDocument]);
  useEffect(() => {
    if (!doc || editing) return;
    let live = true;
    (async () => {
      try {
        const params = pinned
          ? { version: doc.version, employee_id: employeeId }
          : {};
        const r = await api.get(`/document-workflows/${id}/page/${page}`, {
          params,
        });
        if (live) setPreview(r.data);
      } catch (e) {
        if (live) setError(message(e));
      }
    })();
    return () => {
      live = false;
    };
  }, [id, doc, page, pinned, employeeId, editing]);
  useEffect(() => {
    if (!doc || doc.status === "draft" || !employeeId) return;
    let live = true;
    (async () => {
      try {
        const r = await api.get(
          `/document-workflows/${id}/completion/${employeeId}`,
          { params: { version: doc.version } },
        );
        if (live) {
          setCompletion(r.data.completion);
          setCounter(r.data.counter);
          setValues(r.data.counter?.values || r.data.completion?.values || {});
        }
      } catch (e) {
        if (live) setError(message(e));
      }
    })();
    return () => {
      live = false;
    };
  }, [id, doc, employeeId]);
  const run = async (task) => {
    const startedIn = contextRef.current;
    setBusy(true);
    setError("");
    try {
      await task();
    } catch (e) {
      if (contextRef.current === startedIn) setError(message(e));
    } finally {
      if (contextRef.current === startedIn) setBusy(false);
    }
  };
  const responseValues = (signer) =>
    Object.fromEntries(
      Object.entries(values).filter(([key]) =>
        doc.workflow.fields.some(
          (f) =>
            f.id === key &&
            f.signer === signer &&
            !f.read_only &&
            (!f.prefill || f.prefill === "none") &&
            ["text", "date", "checkbox", "choice"].includes(f.kind),
        ),
      ),
    );
  const submit = () =>
    run(async () => {
      const startedIn = contextRef.current;
      const r = await api.post(`/document-workflows/${id}/complete`, {
        expected_version: doc.version,
        values: responseValues("employee"),
        consent,
        typed_name: name,
      });
      if (contextRef.current !== startedIn) return;
      setCompletion(r.data);
      setConsent(false);
      setName("");
    });
  const countersign = () =>
    run(async () => {
      const startedIn = contextRef.current;
      const r = await api.post(
        `/document-workflows/${id}/counter-sign/${employeeId}`,
        {
          expected_version: doc.version,
          values: responseValues("general_manager"),
          consent,
          typed_name: name,
        },
      );
      if (contextRef.current !== startedIn) return;
      setCounter(r.data);
      setConsent(false);
      setName("");
    });
  const wf = doc?.workflow,
    signing = ["sign", "fill_sign"].includes(wf?.completion),
    fill = ["fill", "fill_sign"].includes(wf?.completion),
    canCounter = doc?.can_counter && completion && !counter && wf?.counter_sign;
  return (
    <main className="documents-space">
      <section className="documents-space__vessel">
        <header className="documents-space__header">
          <h1>{doc?.title || "Documents"}</h1>
        </header>
        <div
          className={`document-workflow wf-document-body${editing ? " is-editing" : ""}`}
        >
          {error && (
            <p role="alert" className="wf-error">
              {error}
            </p>
          )}
          {!doc ? (
            <p>Loading document…</p>
          ) : editing ? (
            <DocumentWorkflowBuilder
              existing={doc}
              onBack={() => navigate("/documents")}
              onSaved={() => {
                setEditing(false);
                onSaved?.();
              }}
              onCreated={() => {
                setEditing(false);
                onSaved?.();
              }}
              onCancel={() => setEditing(false)}
            />
          ) : (
            <>
              <div className="wf-editor-tools">
                <Link to="/documents">← Documents</Link>
                <span>
                  Version {doc.version}
                  {doc.status === "draft" ? " · Draft" : ""}
                </span>
                {doc.can_edit && !pinned && (
                  <button onClick={() => setEditing(true)}>
                    Edit template
                  </button>
                )}
                <button
                  disabled={busy}
                  onClick={() =>
                    run(() =>
                      workflowDownload(
                        `/document-workflows/${id}/export${pinned ? `?version=${doc.version}&employee_id=${encodeURIComponent(employeeId)}` : ""}`,
                        "document.pdf",
                      ),
                    )
                  }
                >
                  Download PDF
                </button>
              </div>
              <div className="wf-workspace">
                <WorkflowPage
                  preview={preview}
                  page={page}
                  setPage={setPage}
                  fields={wf.fields}
                  values={{
                    ...completion?.values,
                    ...counter?.values,
                    ...values,
                  }}
                  onValue={(key, value) =>
                    setValues((v) => ({ ...v, [key]: value }))
                  }
                  completion={completion}
                  counter={counter}
                  canFill={
                    !!canCounter || (self && fill && doc.status !== "draft")
                  }
                  activeSigner={canCounter ? "general_manager" : "employee"}
                  canCounterFill={!!canCounter}
                />
                <aside className="wf-settings">
                  {completion ? (
                    <>
                      <p className="wf-status">
                        {counter
                          ? "Completed and countersigned"
                          : wf.counter_sign
                            ? "Awaiting General Manager"
                            : "Completed"}
                      </p>
                      <p>
                        {completion.action} ·{" "}
                        {new Date(completion.completed_at).toLocaleString()}
                      </p>
                      {counter && (
                        <p>
                          GM countersigned ·{" "}
                          {new Date(counter.signature.at).toLocaleString()}
                        </p>
                      )}
                      <button
                        disabled={busy}
                        onClick={() =>
                          run(() =>
                            workflowDownload(
                              `/document-workflows/${id}/completion/${employeeId}/download?version=${doc.version}`,
                              "completed-document.pdf",
                            ),
                          )
                        }
                      >
                        Download completed record
                      </button>
                    </>
                  ) : (
                    <p>
                      {doc.status === "draft"
                        ? "Publish a reviewed version before employees complete it."
                        : wf.completion === "read_only"
                          ? "Read-only document."
                          : self
                            ? "Complete this version while signed in to your own account."
                            : "The employee completes this document in their own account."}
                    </p>
                  )}
                  {((self &&
                    !completion &&
                    doc.status !== "draft" &&
                    wf.completion !== "read_only") ||
                    canCounter) && (
                    <>
                      {(signing || canCounter) && (
                        <label>
                          Your full name
                          <input
                            value={name}
                            onChange={(e) => setName(e.target.value)}
                            maxLength={100}
                            autoComplete="name"
                          />
                        </label>
                      )}
                      <label className="wf-check">
                        <input
                          type="checkbox"
                          checked={consent}
                          onChange={(e) => setConsent(e.target.checked)}
                        />
                        {canCounter
                          ? "I have reviewed this completed version and intend to countersign it as this boutique’s General Manager."
                          : signing
                            ? "I intend to sign this exact document version electronically."
                            : wf.completion === "acknowledge"
                              ? "I acknowledge that I have read this exact document version."
                              : "I confirm that these responses are complete and accurate."}
                      </label>
                      <p className="wf-note">
                        Your authenticated account and the server’s date and
                        time are recorded. Completed versions cannot be
                        overwritten.
                      </p>
                      <button
                        className="wf-primary"
                        disabled={
                          busy ||
                          !preview ||
                          !!error ||
                          !consent ||
                          ((signing || canCounter) && name.trim().length < 2)
                        }
                        onClick={canCounter ? countersign : submit}
                      >
                        {busy
                          ? "Saving…"
                          : canCounter
                            ? "Countersign"
                            : signing
                              ? "Sign document"
                              : wf.completion === "acknowledge"
                                ? "Acknowledge"
                                : "Submit responses"}
                      </button>
                    </>
                  )}
                </aside>
              </div>
            </>
          )}
        </div>
      </section>
      <TotoAdaptiveDock activeView="workspaces" />
    </main>
  );
}
